import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Drawer,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  GetApp as DownloadIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  FileCopy as DuplicateIcon,
  Add as AddIcon,
  HelpOutline as HelpIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import axios from "axios";
import FormBuilder from "../../components/FormBuilder/FormBuilder";
import FormBuilderSteps from "../../components/FormBuilder/FormBuilderSteps";
import RoleManager from "../../components/FormBuilder/RoleManager";
import FormGuidelines from "../../components/FormBuilder/FormGuidelines";
import {
  createForm,
  getFormsByRole,
  updateForm,
  deleteForm,
  publishForm,
  duplicateForm,
  exportFormAsJSON,
  importFormFromJSON,
  getApi,
  updateRole,
  deleteRole,
} from "../../services/formService";
import {
  validateFormSchema,
  generateFormJSON,
} from "../../utils/formSchemaUtils";
import "./FormManager.scss";
import { getRequestWithToken } from "../../utils/ApiRequests";

let AVAILABLE_ROLES = [];

const FormManager = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showRoleManager, setShowRoleManager] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showDeleteRoleConfirm, setShowDeleteRoleConfirm] = useState(false);
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [currentForm, setCurrentForm] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [formSteps, setFormSteps] = useState([]);
  const [editingFormId, setEditingFormId] = useState(null);
  const [newFormRole, setNewFormRole] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [selectedRole, setSelectedRole] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showGuidelines, setShowGuidelines] = useState(false);

  // Load forms on component mount and when role changes
  useEffect(() => {
    loadForms(selectedRole || null);
  }, [selectedRole]);

  // Load all forms or forms by role
  const loadForms = async (role = null) => {
    setLoading(true);
    try {
      const data = await getFormsByRole(role);
      // Handle different response formats
      let formsData = [];
      if (Array.isArray(data)) {
        formsData = data;
      } else if (data.data && Array.isArray(data.data)) {
        formsData = data.data;
      }
      else if (data?.data?.forms && Array.isArray(data?.data?.forms)) {
        formsData = data.data.forms;

      } else if (data.success && Array.isArray(data.result)) {
        formsData = data.result;
      }
      setForms(formsData);
      // showSnackbar(data?.msg, "success");
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to load forms";
      showSnackbar(errorMsg, "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening create form dialog
  const handleOpenCreateDialog = () => {
    setEditingFormId(null);
    setCurrentForm(null);
    setFormFields([]);
    setFormSteps([]);
    setNewFormRole("");
    setShowCreateDialog(true);
  };

  // Handle opening edit form dialog
  const handleEditForm = (form) => {
    console.log(form, " form to be edited");
    setEditingFormId(form._id || form.id);
    setCurrentForm(form);
    setFormFields(form.fields || []);
    setFormSteps(form.steps || []);
    // Set role with both name and _id if available, otherwise just the name
    if (form.roleId || form.role_id) {
      setNewFormRole(JSON.stringify({ name: form.role, _id: form.roleId || form.role_id }));
    } else {
      setNewFormRole(form.role);
    }
    setShowCreateDialog(true);
  };

  // Handle saving form (create/update)
  const handleSaveForm = async () => {
    if (!newFormRole.trim()) {
      showSnackbar("Please select a role", "error");
      return;
    }

    // Check if we're using steps or fields
    const isStepBased = formSteps.length > 0;

    if (!isStepBased && formFields.length === 0) {
      showSnackbar("Please add at least one field or step", "error");
      return;
    }

    if (isStepBased && formSteps.length === 0) {
      showSnackbar("Please add at least one step", "error");
      return;
    }

    try {
      // Parse role object (it's stored as JSON string with {name, _id})
      let roleObj;
      try {
        roleObj = typeof newFormRole === "string" ? JSON.parse(newFormRole) : newFormRole;
      } catch (e) {
        // Fallback if it's just a string (for backward compatibility)
        roleObj = { name: newFormRole };
      }

      // Validate form schema - use role name for validation
      if (!isStepBased) {
        const validation = validateFormSchema({
          role: roleObj.name,
          fields: formFields,
        });

        if (!validation.isValid) {
          showSnackbar(validation.error, "error");
          return;
        }
      }

      // Generate form JSON with role name, then add role._id
      const formData = isStepBased
        ? generateFormJSON(roleObj.name, [], currentForm?.version || 1, formSteps)
        : generateFormJSON(roleObj.name, formFields, currentForm?.version || 1);

      // Add role _id to the payload if it exists
      if (roleObj._id) {
        formData.role_id = roleObj._id;
      }

      console.log(formData, " form data to be saved");

      if (editingFormId) {
        // Update existing form - exclude role from update
        const updateData = { ...formData };
        delete updateData.role;
        delete updateData.version;
        delete updateData.role_id;
        const response = await updateForm(editingFormId, updateData);
        showSnackbar(getResponseMessage(response, "Form updated successfully"), "success");
      } else {
        // Create new form
        const response = await createForm("dynamic-form", formData);
        showSnackbar(getResponseMessage(response, "Form created successfully"), "success");
      }

      setShowCreateDialog(false);
      loadForms(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to save form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle opening delete confirmation
  const handleOpenDeleteConfirm = (formId) => {
    setDeleteTargetId(formId);
    setShowDeleteConfirmDialog(true);
  };

  // Handle deleting form
  const handleDeleteForm = async () => {
    try {
      const response = await deleteForm(deleteTargetId);
      showSnackbar(getResponseMessage(response, "Form deleted successfully"), "success");
      setShowDeleteConfirmDialog(false);
      setDeleteTargetId(null);
      loadForms(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to delete form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle opening preview dialog
  const handlePreviewForm = (form) => {
    setCurrentForm(form);
    setShowPreviewDialog(true);
  };

  // Handle duplicating form
  const handleDuplicateForm = async (form) => {
    try {
      const response = await duplicateForm(form._id || form.id);
      showSnackbar(getResponseMessage(response, "Form duplicated successfully"), "success");
      loadForms(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to duplicate form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle exporting form
  const handleExportForm = (form) => {
    try {
      exportFormAsJSON(form);
      showSnackbar("Form exported successfully", "success");
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to export form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle creating new role
  const handleCreateRole = async (roleData) => {
    try {
      const response = await createForm("plus-role", roleData);
      showSnackbar(getResponseMessage(response, "Role created successfully"), "success");
      setShowRoleManager(false);
      setEditingRole(null);
      loadAllRoles();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to create role";
      showSnackbar(errorMsg, "error");
      console.error("Error creating role:", error);
    }
  };

  // Handle updating role
  const handleUpdateRole = async (roleId, roleData) => {
    try {
      const response = await updateRole(roleId, roleData);
      showSnackbar(getResponseMessage(response, "Role updated successfully"), "success");
      setShowRoleManager(false);
      setEditingRole(null);
      loadAllRoles();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to update role";
      showSnackbar(errorMsg, "error");
      console.error("Error updating role:", error);
    }
  };

  // Handle opening edit role dialog
  const handleEditRole = (role) => {
    setEditingRole(role);
    setShowRoleManager(true);
  };

  // Handle opening delete role confirmation
  const handleOpenDeleteRoleConfirm = (roleId) => {
    setDeleteRoleId(roleId);
    setShowDeleteRoleConfirm(true);
  };

  // Handle deleting role
  const handleDeleteRole = async () => {
    try {
      const response = await deleteRole(deleteRoleId);
      showSnackbar(getResponseMessage(response, "Role deleted successfully"), "success");
      setShowDeleteRoleConfirm(false);
      setDeleteRoleId(null);
      loadAllRoles();
      // If the deleted role was selected, clear the selection
      const deletedRole = AVAILABLE_ROLES.find(r => r._id === deleteRoleId);
      if (deletedRole && selectedRole === deletedRole.name) {
        setSelectedRole("");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to delete role";
      showSnackbar(errorMsg, "error");
      console.error("Error deleting role:", error);
    }
  };

  // Handle importing form
  const handleImportForm = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const form = await importFormFromJSON(file);

      if (!form.role || (!form.fields && !form.steps)) {
        throw new Error("Invalid form format");
      }

      const response = await createForm("dynamic-form", form);
      showSnackbar(getResponseMessage(response, "Form imported successfully"), "success");
      loadForms(form.role);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to import form";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  // Handle publishing form
  const handlePublishToggle = async (form) => {
    try {
      const newStatus = !form.isActive;
      const response = await publishForm(form._id || form.id, newStatus);
      showSnackbar(
        getResponseMessage(response, `Form ${newStatus ? "activated" : "deactivated"} successfully`),
        "success"
      );
      loadForms(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || "Failed to update form status";
      showSnackbar(errorMsg, "error");
      console.error(error);
    }
  };

  const loadAllRoles = async () => {
    try {
      const rolesData = await getRequestWithToken("admin/plus-roles?page=1&limit=100");
      console.log(rolesData.data.data.plusRoles, " rolesData");
      AVAILABLE_ROLES = rolesData.data.data.plusRoles || [];
      // console.log(AVAILABLE_ROLES, " AllRoles");
      showSnackbar(rolesData.message, "success");
    }
    catch (error) {
      console.error("Failed to load roles", error);
    }
  };


  useEffect(() => {
    // let AllRoles = await getApi("plus-roles");
    loadAllRoles();
  }, []);

  // Extract message from API response
  const getResponseMessage = (response, defaultMessage) => {
    return response?.message || response?.msg || response?.data?.message || response?.data?.msg || defaultMessage;
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Open guidelines in new tab
  const openGuidelinesInNewTab = () => {
    // Open guidelines page in new tab
    window.open('/jyplus/form-guidelines', '_blank');
  };

  return (
    <Box className="form-manager-container" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                JY Plus Form Builder
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Build role-based onboarding flows with guided steps and smart validation.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowRoleManager(true)}
                size="small"
              >
                New Role
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenCreateDialog}
              >
                Create Form
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                component="label"
                size="small"
              >
                Import
                <input
                  type="file"
                  accept=".json"
                  hidden
                  onChange={handleImportForm}
                />
              </Button>
              <Button
                variant="outlined"
                startIcon={<HelpIcon />}
                onClick={() => setShowGuidelines(true)}
                size="small"
                color="info"
              >
                Guidelines
              </Button>
              <IconButton
                size="small"
                color="info"
                title="Open Guidelines in New Tab"
                onClick={openGuidelinesInNewTab}
                sx={{
                  border: "1px solid",
                  borderColor: "info.main",
                  borderRadius: 1,
                  height: 36,
                  width: 36,
                  alignSelf: "center",
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Role Filter
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Scope the list by role to focus on specific onboarding flows.
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControl sx={{ minWidth: 200 }} fullWidth>
                    <InputLabel>Select Role</InputLabel>
                    <Select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      label="Select Role"
                    >
                      <MenuItem value="">-- All Roles --</MenuItem>
                      {AVAILABLE_ROLES.map((role) => (
                        <MenuItem key={role._id} value={role.name}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedRole && (
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Edit Role"
                        onClick={() => {
                          const role = AVAILABLE_ROLES.find(r => r.name === selectedRole);
                          if (role) handleEditRole(role);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title="Delete Role"
                        onClick={() => {
                          const role = AVAILABLE_ROLES.find(r => r.name === selectedRole);
                          if (role) handleOpenDeleteRoleConfirm(role._id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, bgcolor: "background.default" }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Builder Playbook
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="1" size="small" color="primary" />
                    <Typography variant="body2">Define roles and data capture steps.</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="2" size="small" color="secondary" />
                    <Typography variant="body2">Preview your form before publishing.</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="3" size="small" color="primary" />
                    <Typography variant="body2">Activate only when the workflow is complete.</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Forms Overview
          </Typography>
          <Chip
            label={`${forms.length} forms`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Stack>

        {loading ? (
          <Box className="loading-container">
            <CircularProgress />
          </Box>
        ) : forms.length === 0 ? (
          <Paper className="empty-state" variant="outlined" sx={{ p: 3 }}>
            <Typography color="textSecondary">
              {selectedRole
                ? `No forms found for the "${selectedRole}" role`
                : "No forms available"}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow className="table-header">
                  <TableCell>Role</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell align="center">Structure</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map((form) => (
                  <TableRow key={form._id || form.id} hover>
                    <TableCell className="role-cell">
                      <Stack spacing={0.3}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {form.role}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Updated v{form.version || 1}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>v{form.version || 1}</TableCell>
                    <TableCell align="center">
                      {form.steps && form.steps.length > 0 ? (
                        <Chip
                          label={`${form.steps.length} steps`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ) : (
                        <Chip
                          label={`${form.fields?.length || 0} fields`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={form.isActive ? "Active" : "Inactive"}
                        color={form.isActive ? "success" : "default"}
                        size="small"
                        onClick={() => handlePublishToggle(form)}
                        clickable
                      />
                    </TableCell>
                    <TableCell align="center" className="actions-cell">
                      <IconButton
                        size="small"
                        title="Preview"
                        onClick={() => handlePreviewForm(form)}
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Edit"
                        onClick={() => handleEditForm(form)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Export"
                        onClick={() => handleExportForm(form)}
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Delete"
                        onClick={() => handleOpenDeleteConfirm(form._id || form.id)}
                        className="delete-icon"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Form Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={(event, reason) => {
          // Only close if Cancel button is clicked (reason will be undefined)
          // Block closing from backdrop (reason = "backdropClick")
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowCreateDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingFormId ? "Edit Form" : "Create New Form"}
        </DialogTitle>
        <DialogContent className="form-dialog-content">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            {editingFormId ? (
              // When editing, show role as a text field instead of select
              <TextField
                label="Role"
                value={
                  typeof newFormRole === "string"
                    ? (() => {
                      try {
                        const roleObj = JSON.parse(newFormRole);
                        return roleObj.name || newFormRole;
                      } catch {
                        return newFormRole;
                      }
                    })()
                    : newFormRole
                }
                fullWidth
                disabled
                variant="outlined"
              />
            ) : (
              // When creating, show role as a select
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newFormRole}
                  onChange={(e) => setNewFormRole(e.target.value)}
                  label="Role"
                >
                  {AVAILABLE_ROLES.map((role) => (
                    <MenuItem key={role._id} value={JSON.stringify({ name: role.name, _id: role._id })}>
                      {role.name.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {newFormRole && (
              <FormBuilderSteps
                initialSteps={formSteps}
                onStepsChange={setFormSteps}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveForm}
            variant="contained"
            color="primary"
          >
            {editingFormId ? "Update Form" : "Create Form"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Form Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={(event, reason) => {
          // Only close if Close button is clicked (reason will be undefined)
          // Block closing from backdrop (reason = "backdropClick")
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowPreviewDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Preview Form - {currentForm?.role}</DialogTitle>
        <DialogContent>
          {currentForm && (
            <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Step-based form preview */}
              {currentForm.steps && currentForm.steps.length > 0 ? (
                currentForm.steps.map((step, stepIndex) => (
                  <Box key={stepIndex} sx={{ mb: 3 }}>
                    <Paper sx={{ p: 2, bgcolor: "#f5f5f5", mb: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600, mb: 0.5 }}>
                        Step {stepIndex + 1}: {step.title}
                      </Typography>
                      {step.description && (
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {step.description}
                        </Typography>
                      )}
                    </Paper>

                    {/* Fields in this step */}
                    {step.fields && step.fields.length > 0 ? (
                      step.fields.map((field, fieldIndex) => (
                        <Box key={fieldIndex} sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              display: "flex",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            {field.label}
                            {field.required && <span style={{ color: "red" }}>*</span>}
                          </Typography>

                          {/* Field Preview based on type */}
                          {field.type === "text" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" variant="outlined" />
                          )}
                          {field.type === "email" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" type="email" variant="outlined" />
                          )}
                          {field.type === "number" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" type="number" variant="outlined" />
                          )}
                          {field.type === "textarea" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" multiline rows={3} variant="outlined" />
                          )}
                          {field.type === "dropdown" && (
                            <Select disabled displayEmpty size="small">
                              <MenuItem value=""><em>Select {field.label}</em></MenuItem>
                              {field.options?.map((option, idx) => (
                                <MenuItem key={idx} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </Select>
                          )}
                          {field.type === "radio" && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              {field.options?.map((option, idx) => (
                                <FormControlLabel key={idx} disabled control={<input type="radio" disabled />} label={option.label} />
                              ))}
                            </Box>
                          )}
                          {field.type === "multiselect" && (
                            <FormControlLabel disabled control={<input type="multiselect" disabled />} label={field.label} />
                          )}
                          {field.type === "boolean" && (
                            <Box sx={{ display: "flex", gap: 2 }}>
                              <FormControlLabel disabled control={<input type="radio" disabled />} label="Yes" />
                              <FormControlLabel disabled control={<input type="radio" disabled />} label="No" />
                            </Box>
                          )}
                          {field.type === "date" && (
                            <TextField placeholder={field.label} fullWidth disabled size="small" type="date" variant="outlined" InputLabelProps={{ shrink: true }} />
                          )}
                          {field.type === "file" && (
                            <Box sx={{ border: "2px dashed #ccc", borderRadius: 1, p: 2, textAlign: "center", backgroundColor: "#f9f9f9", cursor: "not-allowed", opacity: 0.7 }}>
                              <Typography variant="body2" color="textSecondary">📁 Click to upload {field.label}</Typography>
                            </Box>
                          )}

                          <Typography variant="caption" color="textSecondary">
                            Field Key: <code>{field.key}</code>
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Alert severity="info" sx={{ mb: 2 }}>No fields in this step</Alert>
                    )}
                  </Box>
                ))
              ) : currentForm.fields ? (
                /* Fallback: Field-based form preview */
                currentForm.fields.map((field, index) => (
                  <Box key={index} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                      }}
                    >
                      {field.label}
                      {field.required && <span style={{ color: "red" }}>*</span>}
                    </Typography>

                    {field.type === "text" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" variant="outlined" />
                    )}
                    {field.type === "email" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" type="email" variant="outlined" />
                    )}
                    {field.type === "number" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" type="number" variant="outlined" />
                    )}
                    {field.type === "textarea" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" multiline rows={3} variant="outlined" />
                    )}
                    {field.type === "dropdown" && (
                      <Select disabled displayEmpty size="small">
                        <MenuItem value=""><em>Select {field.label}</em></MenuItem>
                        {field.options?.map((option, idx) => (
                          <MenuItem key={idx} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    )}
                    {field.type === "radio" && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {field.options?.map((option, idx) => (
                          <FormControlLabel key={idx} disabled control={<input type="radio" disabled />} label={option.label} />
                        ))}
                      </Box>
                    )}
                    {field.type === "multiselect" && (
                      <FormControlLabel disabled control={<input type="multiselect" disabled />} label={field.label} />
                    )}
                    {field.type === "boolean" && (
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <FormControlLabel disabled control={<input type="radio" disabled />} label="Yes" />
                        <FormControlLabel disabled control={<input type="radio" disabled />} label="No" />
                      </Box>
                    )}
                    {field.type === "date" && (
                      <TextField placeholder={field.label} fullWidth disabled size="small" type="date" variant="outlined" InputLabelProps={{ shrink: true }} />
                    )}
                    {field.type === "file" && (
                      <Box sx={{ border: "2px dashed #ccc", borderRadius: 1, p: 2, textAlign: "center", backgroundColor: "#f9f9f9", cursor: "not-allowed", opacity: 0.7 }}>
                        <Typography variant="body2" color="textSecondary">📁 Click to upload {field.label}</Typography>
                      </Box>
                    )}

                    <Typography variant="caption" color="textSecondary">
                      Field Key: <code>{field.key}</code>
                    </Typography>
                  </Box>
                ))
              ) : (
                <Alert severity="warning">No form structure available</Alert>
              )}

              {/* Form metadata */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  borderLeft: "4px solid #1976d2",
                }}
              >
                <Typography variant="caption" display="block">
                  <strong>Role:</strong> {currentForm.role}
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Version:</strong> {currentForm.version}
                </Typography>
                {currentForm.steps && currentForm.steps.length > 0 ? (
                  <Typography variant="caption" display="block">
                    <strong>Total Steps:</strong> {currentForm.steps.length}
                  </Typography>
                ) : (
                  <Typography variant="caption" display="block">
                    <strong>Total Fields:</strong> {currentForm.fields?.length || 0}
                  </Typography>
                )}
                <Typography variant="caption" display="block">
                  <strong>Status:</strong>{" "}
                  <Chip
                    label={currentForm.isActive ? "Active" : "Inactive"}
                    color={currentForm.isActive ? "success" : "default"}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirmDialog}
        onClose={(event, reason) => {
          // Only close if Cancel button is clicked (reason will be undefined)
          // Block closing from backdrop (reason = "backdropClick")
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowDeleteConfirmDialog(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Delete Form</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this form? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteForm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* RoleManager Dialog */}
      <RoleManager
        open={showRoleManager}
        onClose={() => {
          setShowRoleManager(false);
          setEditingRole(null);
        }}
        onCreateRole={handleCreateRole}
        editingRole={editingRole}
        onUpdateRole={handleUpdateRole}
      />

      {/* Delete Role Confirmation Dialog */}
      <Dialog
        open={showDeleteRoleConfirm}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowDeleteRoleConfirm(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this role? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteRoleConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRole}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Guidelines Drawer */}
      <Drawer
        anchor="right"
        open={showGuidelines}
        onClose={() => setShowGuidelines(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '400px', md: '400px' },
            boxSizing: 'border-box',
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "2px solid #e0e0e0",
          bgcolor: "#f5f5f5",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HelpIcon color="info" fontSize="large" />
            <Typography variant="h5" fontWeight={600}>Form Builder Guidelines</Typography>
          </Box>
          <IconButton onClick={() => setShowGuidelines(false)} size="medium">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Drawer Content */}
        <Box sx={{ p: 3, overflowY: "auto", height: "100%" }}>
          <FormGuidelines />
        </Box>

        {/* Drawer Footer */}
        <Box sx={{
          p: 2,
          borderTop: "2px solid #e0e0e0",
          bgcolor: "#f5f5f5",
          position: "sticky",
          bottom: 0,
          display: "flex",
          justifyContent: "flex-end"
        }}>
          <Button
            // onClick={() => setShowGuidelines(false)}
            onClick={() => handleOpenCreateDialog()}
            variant="contained"
            size="large"
            fullWidth
          >
            Got it! Create Form
          </Button>
        </Box>
      </Drawer>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormManager;
